import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import asteriskService from '../../services/asteriskService.js';

export default function MusicOnHoldManager() {
  const navigate = useNavigate();
  const [musicOnHoldInfo, setMusicOnHoldInfo] = useState(null);
  const [musicOnHoldFile, setMusicOnHoldFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadMusicOnHoldInfo = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const info = await asteriskService.getMusicOnHoldInfo();
      setMusicOnHoldInfo(info);
    } catch (err) {
      setMusicOnHoldInfo(null);
      setError(
        err?.response?.data?.error || err?.message || 'Failed to load music on hold file information.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMusicOnHoldInfo();
  }, [loadMusicOnHoldInfo]);

  const replaceMusicOnHold = useCallback(async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    setError('');
    setSuccess('');

    if (!musicOnHoldFile) {
      setError('Select an audio file first.');
      return;
    }

    const requiredExtension = String(musicOnHoldInfo?.requiredExtension || '').toLowerCase();
    if (requiredExtension && !musicOnHoldFile.name.toLowerCase().endsWith(requiredExtension)) {
      setError(`Select a ${requiredExtension.toUpperCase()} file.`);
      return;
    }
    if (
      musicOnHoldInfo?.maxUploadBytes &&
      musicOnHoldFile.size > Number(musicOnHoldInfo.maxUploadBytes)
    ) {
      setError(
        `File must not exceed ${Math.round(Number(musicOnHoldInfo.maxUploadBytes) / 1024 / 1024)} MB.`
      );
      return;
    }
    if (!window.confirm(`Replace ${musicOnHoldInfo?.fileName || 'the current music on hold file'}?`)) {
      return;
    }

    setSaving(true);
    try {
      const result = await asteriskService.replaceMusicOnHold(musicOnHoldFile);
      setMusicOnHoldInfo((prev) => ({
        ...prev,
        configured: true,
        ...(result?.file || {})
      }));
      setMusicOnHoldFile(null);
      setSuccess(result?.message || 'Music on hold file replaced successfully.');
      formElement.reset();
    } catch (err) {
      setError(
        err?.response?.data?.error || err?.message || 'Music on hold file replacement failed.'
      );
    } finally {
      setSaving(false);
    }
  }, [musicOnHoldFile, musicOnHoldInfo]);

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
        <div>
          <h4 className="mb-1">Music on Hold</h4>
          <small className="text-muted">Replace the audio file used by Asterisk for music on hold.</small>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-danger" onClick={() => navigate('/logout')}>
            <i className="bi bi-box-arrow-right me-1"></i>
            Logout
          </button>
          <button className="btn btn-outline-primary" onClick={() => navigate('/admin/users')}>
            <i className="bi bi-people me-1"></i>
            Users
          </button>
          <button className="btn btn-outline-primary" onClick={() => navigate('/admin/generic')}>
            <i className="bi bi-list-ul me-1"></i>
            Generic
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={loadMusicOnHoldInfo}
            disabled={loading || saving}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      <div className="border rounded bg-white shadow-sm p-3">
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-3">
          <div>
            <h5 className="mb-1">Replace Music on Hold File</h5>
            <small className="text-muted">The uploaded file replaces the current configured file.</small>
          </div>
          {musicOnHoldInfo?.fileName && (
            <div className="text-end">
              <div className="fw-semibold">{musicOnHoldInfo.fileName}</div>
              <small className="text-muted">
                {(Number(musicOnHoldInfo.size || 0) / 1024 / 1024).toFixed(2)} MB
                {musicOnHoldInfo.updatedAt
                  ? ` | Updated ${new Date(musicOnHoldInfo.updatedAt).toLocaleString()}`
                  : ''}
              </small>
            </div>
          )}
        </div>

        {error && <div className="alert alert-danger py-2">{error}</div>}
        {success && <div className="alert alert-success py-2">{success}</div>}

        <form className="row g-2 align-items-end" onSubmit={replaceMusicOnHold}>
          <div className="col-md-9">
            <label className="form-label" htmlFor="music-on-hold-file">Audio file</label>
            <input
              id="music-on-hold-file"
              className="form-control"
              type="file"
              accept={musicOnHoldInfo?.requiredExtension || '.wav,.mp3'}
              disabled={loading || saving || !musicOnHoldInfo?.configured}
              onChange={(event) => {
                setMusicOnHoldFile(event.target.files?.[0] || null);
                setError('');
                setSuccess('');
              }}
            />
          </div>
          <div className="col-md-3 d-grid">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || saving || !musicOnHoldInfo?.configured || !musicOnHoldFile}
            >
              <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-upload'} me-1`}></i>
              {saving ? 'Replacing...' : 'Replace File'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
